import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const getAuthConfig = (signal = null) => {
  const token = localStorage.getItem("token");
  const config = { withCredentials: true };
  if (token) config.headers = { Authorization: `Bearer ${token}` };
  if (signal) config.signal = signal;
  return config;
};

/**
 * Fetch projects for the current user.
 *
 * Overloaded usage for backward compatibility:
 * - fetchProjects(signal)
 * - fetchProjects(mode, signal)
 *
 * Where mode is "dashboard" or "debate".
 */
export const fetchProjects = async (modeOrSignal = null, maybeSignal = null) => {
  let mode = null;
  let signal = null;

  if (typeof modeOrSignal === "string") {
    mode = modeOrSignal;
    signal = maybeSignal;
  } else {
    signal = modeOrSignal;
  }

  try {
    const config = getAuthConfig(signal);
    if (mode) {
      config.params = { ...(config.params || {}), mode };
    }

    const response = await axios.get(`${API_URL}/ai/projects`, config);
    return response.data.projects || [];
  } catch (error) {
    if (
      error.response?.status === 401 ||
      error.name === "AbortError" ||
      error.name === "CanceledError"
    ) {
      return [];
    }
    // Network or other errors: log and fail gracefully with empty list
    console.error("Error fetching projects:", error);
    return [];
  }
};

/**
 * Fetch archived projects for the current user.
 */
export const fetchArchivedProjects = async (signal = null) => {
  try {
    const config = getAuthConfig(signal);
    const response = await axios.get(`${API_URL}/ai/projects-archived`, config);
    return response.data.projects || [];
  } catch (error) {
    if (
      error.response?.status === 401 ||
      error.name === "AbortError" ||
      error.name === "CanceledError"
    ) {
      return [];
    }
    console.error("Error fetching archived projects:", error);
    return [];
  }
};

/**
 * Create a new project.
 *
 * Optional mode parameter allows creating dashboard- or debate-specific projects.
 * Defaults to "dashboard" for backward compatibility.
 */
export const createProject = async (name, mode = "dashboard") => {
  if (!name || !name.trim()) throw new Error("Project name is required");
  const config = getAuthConfig();
  const response = await axios.post(
    `${API_URL}/ai/projects`,
    { name: name.trim(), mode },
    config
  );
  return response.data.project;
};

export const updateProject = async (projectId, name) => {
  if (!projectId) throw new Error("Project ID is required");
  if (!name || !name.trim()) throw new Error("Project name is required");
  const config = getAuthConfig();
  const response = await axios.put(
    `${API_URL}/ai/projects/${projectId}`,
    { name: name.trim() },
    config
  );
  return response.data.project;
};

export const archiveProject = async (projectId, archived = true) => {
  if (!projectId) throw new Error("Project ID is required");
  const config = getAuthConfig();
  const response = await axios.put(
    `${API_URL}/ai/projects/${projectId}`,
    { archived },
    config
  );
  return response.data.project;
};

export const deleteProject = async (projectId) => {
  if (!projectId) throw new Error("Project ID is required");
  const config = getAuthConfig();
  await axios.delete(`${API_URL}/ai/projects/${projectId}`, config);
};
